import React, {
	useMemo, useEffect, useState, useRef,
} from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import Editor from 'draft-js-plugins-editor';
import createImagePlugin from 'draft-js-image-plugin';
import {
	EditorState, convertFromRaw, ContentBlock, ContentState, convertToRaw, RawDraftContentState 
} from 'draft-js';
import { TeleprompterSectionInterface } from '../../../types/interfaces';
import { ShotFeed, SHOT_THUMBNAIL_TYPES } from '../../../stores/reducers/shots';
import { MediaItem, SlideNote } from '../../../stores/reducers/media';
import { GreenroomState } from '../../../stores/types/types';
import { TELEPROMPTER_SECTION_TYPES, TELEPROMPTER_SECTION_STATES } from '../../../types/constants';
import Socket from '../../../connection/socket';
import { durationString, uniqueArray } from '../../../utils/utils';
import colors from '../../../utils/colors';
import usePrevious from '../../../utils/use-previous';
import TeleprompterSectionEditor from './teleprompter-section-editor';
import ShotThumbnail from '../../general-components/shot-thumbnail';
import Icon, { COLORS, ICONS } from '../../general-components/icon';
import useTeleprompters from '../../../hooks/use-teleprompters';

interface Props {
	teleprompterId: number;
	teleprompterSection: TeleprompterSectionInterface;
	selectedSection: boolean;
	setSelectedSection: Function;
	setMediaItem: Function;
	sectionState?: number;
	socket: Socket | null;
	canEdit: boolean;
	mediaItem: MediaItem | null;
	content?: RawDraftContentState;
}

interface StyleMap {
	[key: string]: Object;
}

const imagePlugin = createImagePlugin();

const fontColors: any = {
	WHITE: { color: colors.nearWhite },
	BLUE: { color: colors.textBlue },
	GREEN: { color: colors.textGreen },
	YELLOW: { color: colors.textYellow },
};

const styleMap = (function () {
	const len = 48;
	const map: StyleMap = {};

	// create font size map
	for (let i = 10; i <= len; i += 1) {
		map[i] = { fontSize: i };
	}

	return {
		...map,
		...fontColors,
	};
}());

const getBlockStyle = (block: ContentBlock): string => block.getType();

export default function TeleprompterSection(props: Props) {
	const {
		teleprompterId,
		teleprompterSection,
		teleprompterSection: {
			section_type_id: sectionTypeId,
			section_type: sectionType,
			section_data: {
				section_title: sectionTitle,
				shotObject,
				media_url: mediaUrl,
			},
		},
		selectedSection,
		setSelectedSection,
		setMediaItem,
		mediaItem,
		sectionState = TELEPROMPTER_SECTION_STATES.NOT_SELECTED,
		socket,
		canEdit,
		content
	} = props;

	const {
		createTeleprompterSections,
	} = useTeleprompters(socket);

	const media = useSelector((state: GreenroomState) => state.MediaReducer.media);
	const shots = useSelector((state: GreenroomState) => state.ShotsReducer.shots);

	const [sectionId, setSectionId] = useState<number>(teleprompterSection?.id ?? 0);
	const [mediaForShot, setMediaForShot] = useState<MediaItem[]>([]);
	const [editorState, setEditorState] = useState<EditorState>(
		!content
			? EditorState.createEmpty()
			: EditorState.createWithContent(convertFromRaw(content)),
	);

	const editor = useRef<any>(null);
	const sectionEl = useRef<HTMLDivElement | null>(null);
	const prevSectionType = usePrevious(sectionType);

	const isFirstSection = useMemo(() => {
		if (sectionType === TELEPROMPTER_SECTION_TYPES.SHOT) {
			return shots[0].shot_id === teleprompterSection.section_type_id;
		}
		const mediaTest = media.find(
			(m: MediaItem) => m.id === teleprompterSection.section_type_id,
		);
		const imageArray = mediaTest?.images?.split(',') ?? [];
		return imageArray[0] === teleprompterSection.section_data.media_url;
	}, [shots, media, teleprompterSection]);

	const shotMedia = useMemo(() => mediaForShot.map((m: MediaItem, index: number) => (
			<div key={index}
				className="flex-align-center justify-content-space-between background-light background-highlight border-radius-12 padding-medium margin-top-medium cursor-pointer"
				onClick={() => setMediaItem(m)}
			>
				<div className="flex-column width-full overflow-hidden">
					<span className="text-gray font-size-12 font-weight-normal line-height-16 margin-bottom-extra-small">Slide notes</span>
					<span className="font-weight-heavy line-height-20 truncate-overflow">{m.custom_name || m.original_name}</span>
				</div>
				<Icon name={ICONS.ARROW_RIGHT} color={COLORS.LIGHT_GRAY} size={16} />
			</div>
	)), [mediaForShot]);

	const titleText = useMemo(() => {
		if (sectionType === TELEPROMPTER_SECTION_TYPES.SHOT) {
			return (
				<span className={classNames('font-weight-heavy truncate-overflow', !sectionTitle && 'text-light')}>
					{sectionTitle || 'Unnamed Shot'}
				</span>
			);
		}
		return <span className="font-weight-heavy truncate-overflow">{sectionTitle}</span>;
	}, [sectionType, sectionTitle]);

	const handleSelectSection = async () => {
		if (canEdit) {
			// if this section does not have content, it does not exist in the database.
			// create it and get the id before opening the editor.
			if (!teleprompterSection.content) {
				const newTele = await createTeleprompterSections(
					teleprompterId,
					{
						teleprompter_id: teleprompterSection.teleprompter_id,
						section_data: teleprompterSection.section_data,
						content: JSON.stringify(convertToRaw(editorState.getCurrentContent())),
						section_type_id: teleprompterSection.section_type_id,
						section_type: teleprompterSection.section_type,
					},
				);
				if (newTele?.id) {
					setSectionId(newTele.id);
				}
			}
			if (sectionType === TELEPROMPTER_SECTION_TYPES.SHOT) {
				setSelectedSection(sectionTypeId);
			} else {
				setSelectedSection(mediaUrl);
			}
		}
	};

	useEffect(() => {
		const isSlideMedia = (mediaItem?.media_data?.image_array?.length ?? 1) > 1;
		const parsedContent = content ? JSON.parse(JSON.stringify(content)) : null;

		if (isSlideMedia && !parsedContent) {
			const notesKey = isSlideMedia && mediaUrl
				? mediaItem?.media_data?.notes_map?.[mediaUrl]
				: -1;
			const note: SlideNote = isSlideMedia && (notesKey ?? -1) > -1
				? mediaItem?.notes?.find((n: SlideNote) => n.key === notesKey) ?? { key: -1, notes: '' }
				: { key: -1, notes: '' };
			if (note.notes) {
				if (parsedContent && parsedContent.blocks[0].text) {
					parsedContent.blocks[0].text = parsedContent.blocks[0].text ?? note.notes;
				} else {
					const newContent = ContentState.createFromText(note.notes);
					setEditorState(EditorState.createWithContent(newContent));
				}
			}
		}

		if (parsedContent) {
			setEditorState(EditorState.createWithContent(convertFromRaw(parsedContent)));
		}
	}, [teleprompterSection]);

	useEffect(() => {
		if (!shotObject) {
			setMediaForShot([]);
			return;
		}

		const mediaObjectsInShot = shotObject.shot_data.shotFeeds
			.filter((feed: ShotFeed) => media.some(
				(m: MediaItem) => feed.media === m.id && m.num_pages > 1,
			)).map((feed: ShotFeed) => media.find(
				(m: MediaItem) => feed.media === m.id,
			));

		setMediaForShot(uniqueArray('id', mediaObjectsInShot));
	}, [shotObject?.shot_data?.shotFeeds, media]);

	useEffect(() => {
		const tabHolder = document.getElementsByClassName('tab active')[0];
		if (
			(
				sectionState === TELEPROMPTER_SECTION_STATES.SELECTED
				|| sectionState === TELEPROMPTER_SECTION_STATES.PLAYING
			) && tabHolder
		) {
			setTimeout(() => {
				if (sectionEl.current) {
					// if the teleprompter section in the canvas is the first section, we scroll to the
					// top of the container to see the header when going back and forth from shots and
					// slide decks, we don't need to see the scrolling animation
					if (isFirstSection) {
						tabHolder.scrollTo({
							top: 0,
							behavior: sectionType !== prevSectionType ? 'auto' : 'smooth',
						});
					} else {
						tabHolder.scrollTo({
							top: sectionEl.current.offsetTop,
							behavior: sectionType !== prevSectionType ? 'auto' : 'smooth',
						});
					}
				}
			});
		}
	}, [sectionType, sectionState, isFirstSection]);

	return (
		<div className={classNames('section-container flex-column padding-top-medium')} ref={sectionEl}>
			<div className={classNames('section-data width-full background-light padding-small', sectionState === TELEPROMPTER_SECTION_STATES.PLAYING && 'playing')}>
				{shotObject
					&& <ShotThumbnail
						key={sectionTypeId}
						shot={shotObject}
						borderRatio={0.1}
						onSelect={() => { }}
						shotThumbnailType={SHOT_THUMBNAIL_TYPES.TELEPROMPTER}
					/>
				}
				{mediaUrl
					&& <div
						className={classNames('media-thumbnail', mediaItem?.media_data.fill && 'fill')}
						style={{ background: mediaItem?.media_data.background_color }}
					>
						<img
							src={mediaItem?.media_data.thumbnail_map[mediaUrl]}
							alt={''}
							onError={(event: React.ChangeEvent<HTMLImageElement>) => {
								/* eslint-disable-next-line no-param-reassign */
								event.target.style.visibility = 'hidden';
							}}
						/>
					</div>
				}
				<div className="flex-column flex-justify-center padding-left-medium padding-right-medium overflow-hidden">
					{titleText}
					{shotObject?.shot_data.shotDuration
						|| sectionState === TELEPROMPTER_SECTION_STATES.PLAYING
						? (
							<div className="font-weight-heavy truncate-overflow">
								{shotObject?.shot_data.shotDuration ? <span className="text-gray font-size-12 font-weight-normal">{durationString(shotObject.shot_data.shotDuration)}</span> : null}
								{sectionState === TELEPROMPTER_SECTION_STATES.PLAYING && (
									<>
										{shotObject?.shot_data.shotDuration ? <span className="text-gray font-size-12 font-weight-normal"> • </span> : null}
										<span className="text-green font-size-12 font-weight-normal">LIVE</span>
									</>
								)}
							</div>
						) : null
					}
				</div>
			</div>
			{shotMedia}
			{selectedSection
				? (
					<TeleprompterSectionEditor
						teleprompterId={teleprompterId}
						sectionId={sectionId}
						teleprompterSection={teleprompterSection}
						selectedSection={selectedSection}
						setSelectedSection={setSelectedSection}
						socket={socket}
						editorState={editorState}
						setEditorState={setEditorState}
					/>
				) : (
					<div className={classNames('padding-top-small padding-bottom-medium width-full editor', `section-editor-${teleprompterSection.id}`)}>
						<div onClick={handleSelectSection}>
							<Editor
								ref={editor}
								editorState={editorState}
								onChange={() => { }}
								plugins={[imagePlugin]}
								customStyleMap={styleMap}
								blockStyleFn={getBlockStyle}
								placeholder={canEdit ? 'Start typing your text...' : ''}
								readOnly={true}
							/>
						</div>
					</div >
				)}
		</div>
	);
}
